import {
  PULSE_PRESETS,
  PulseCandidate,
  PulseCandidateDump,
  PulseCard,
  PulseChart,
  PulsePersona,
  PulsePreviewResult,
  PulseReceipt,
  PulseStudioExplainRequest,
  PulseTicketRow
} from "@swivl-mono/types";

export interface PulsePersonaOption {
  label: string;
  value: PulsePersona;
}

/** The wave-1 personas, in the catalog's order. */
export function pulsePersonaOptions(): PulsePersonaOption[] {
  return Object.values(PULSE_PRESETS).map((preset) => ({ label: preset.label, value: preset.persona }));
}

/** Select values are strings, so "no persona" needs one of its own. */
export const NO_PULSE_PERSONA = "none";

export interface MemberPersonaOption {
  label: string;
  /** A persona, or NO_PULSE_PERSONA. */
  value: string;
}

/** The personas plus "No persona", for the Members page and the invite form (ENG-3433). */
export function memberPersonaOptions(): MemberPersonaOption[] {
  return [{ label: "No persona", value: NO_PULSE_PERSONA }, ...pulsePersonaOptions()];
}

/** The persona a select value stands for; null for "No persona" or anything unknown. */
export function personaFromOption(value: string | undefined): PulsePersona | null {
  return Object.values(PulsePersona).find((persona) => persona === value) ?? null;
}

/** A receipt's cost in one line: tokens by type, then how long the call took. */
export function receiptSummary(receipt: PulseReceipt): string {
  const tokens = (count: number): string => count.toLocaleString("en-US");
  return [
    `${tokens(receipt.inputTokens)} in`,
    `${tokens(receipt.outputTokens)} out`,
    `${tokens(receipt.cacheReadInputTokens)} cache read`,
    `${tokens(receipt.cacheCreationInputTokens)} cache write`,
    `${(receipt.latencyMs / 1000).toFixed(1)} s`
  ].join(" · ");
}

/** A chart in words — its kind and numbers — so staff can check it before phase 3 draws it. Null means not enough data. */
export function chartSummary(chart: PulseChart): string {
  const value = (n: number | null): string => (n === null ? "not enough data" : String(n));
  switch (chart.kind) {
    case "compare":
      return `compare · ${value(chart.value)} against ${value(chart.baseline)} (${chart.unit})`;
    case "bars":
      return `bars · ${chart.bars.map((bar) => `${bar.label} ${bar.value}`).join(", ") || "none"} (${chart.unit})`;
    case "mix":
      return `mix · ${chart.parts.map((part) => `${part.label} ${part.value}`).join(", ") || "none"}`;
    case "columns":
      return `columns · ${chart.keys.join(" / ")} · ${chart.points.length} points`;
    case "sparkline":
      return `sparkline · ${chart.points.length} points`;
  }
}

/** A candidate as the top task's options name it: the signal, then its subject. */
export function candidateLabel(candidate: PulseCandidate): string {
  return `${candidate.signalLabel} · ${candidate.subject.label}`;
}

/** The member's tickets from the preview's facts, by id, so My Work rows show a number and title. Empty when the tickets couldn't be loaded. */
export function ticketsById(dump: PulseCandidateDump): Map<number, PulseTicketRow> {
  const rows = dump.memberFacts.data?.myTickets.data?.rows ?? [];
  return new Map(rows.map((row) => [row.id, row]));
}

/** Explain for a preview card: the card and its candidate travel with the request, since a preview is never saved. Null when the candidate is missing. */
export function previewExplainRequest(card: PulseCard, dump: PulseCandidateDump, persona: PulsePersona): PulseStudioExplainRequest | null {
  const candidate = dump.candidates.find((c) => c.id === card.id);
  return candidate ? { persona, card, candidate } : null;
}

/** An Anthropic error as the SDK words it, "401 {json}", made readable: "API key is invalid. (401)". Anything else is returned as it is. */
export function readableAIError(message: string): string {
  const match = /^(\d{3}) (\{.*\})$/s.exec(message);
  if (!match) return message;
  try {
    const body: unknown = JSON.parse(match[2]);
    const inner = typeof body === "object" && body !== null && "error" in body ? body.error : null;
    const text = typeof inner === "object" && inner !== null && "message" in inner ? inner.message : null;
    return typeof text === "string" ? `${text} (${match[1]})` : message;
  } catch {
    return message;
  }
}

/** Why the panel has no page to show, for the line beside Preview: the request's own error, or the AI call's. Null when there's nothing to explain. */
export function previewProblem(error: string | null, result: PulsePreviewResult | null): string | null {
  if (error) return error;
  if (result && !result.board) return `The AI call failed: ${readableAIError(result.receipt.error ?? "no reason was recorded")}`;
  return null;
}
